import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Code, ArrowLeft, Pause, Play, XCircle, Calendar, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';
import api from '../api';

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptions = async () => {
    try {
      const response = await api.get('/subscriptions');
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const handleSubscriptionAction = async (subscriptionId, action) => {
    const confirmMessage = {
      pause: 'Are you sure you want to pause this subscription?',
      cancel: 'Are you sure you want to cancel this subscription? This action cannot be undone.',
      resume: 'Resume this subscription?'
    };

    if (!window.confirm(confirmMessage[action])) return;

    try {
      await api.patch(`/subscriptions/${subscriptionId}`, { action });
      toast.success(`Subscription ${action}d successfully`);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} subscription`);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-100 text-emerald-700',
      paused: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700',
      expired: 'bg-gray-100 text-gray-700'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active');
  const inactiveSubscriptions = subscriptions.filter(s => s.status !== 'active');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center space-x-2">
              <Code className="w-6 h-6 text-violet-600" />
              <span className="text-lg font-bold text-slate-900">CodeITT</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Subscription Management</h1>
          <p className="text-slate-600">Manage your active subscriptions and class packages</p>
        </div>

        {/* Active Subscriptions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Active Subscriptions</h2>
          {activeSubscriptions.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
              <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Active Subscriptions</h3>
              <p className="text-slate-600 mb-6">Subscribe to a class package to start learning</p>
              <Button onClick={() => navigate('/payment')} className="bg-violet-600 hover:bg-violet-700">
                Browse Packages
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeSubscriptions.map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl border-2 border-violet-200 p-6 shadow-lg">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{sub.package_name}</h3>
                      <p className="text-sm text-slate-600">Started {new Date(sub.created_at).toLocaleDateString()}</p>
                    </div>
                    {getStatusBadge(sub.status)}
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Classes Used</span>
                        <span className="font-medium text-slate-900">
                          {sub.classes_used} / {sub.classes_total}
                        </span>
                      </div>
                      <Progress value={(sub.classes_used / sub.classes_total) * 100} className="h-2" />
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-600">Remaining</p>
                        <p className="text-2xl font-bold text-violet-600">{sub.classes_remaining}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Monthly</p>
                        <p className="text-2xl font-bold text-slate-900">${sub.monthly_amount}</p>
                      </div>
                    </div>

                    {sub.next_billing_date && (
                      <div className="bg-violet-50 rounded-lg p-3">
                        <p className="text-xs text-slate-600">Next Billing Date</p>
                        <p className="text-sm font-medium text-slate-900">
                          {new Date(sub.next_billing_date).toLocaleDateString('en-US', {
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Button
                      onClick={() => navigate(`/bookings?subscription=${sub.id}`)}
                      className="w-full bg-violet-600 hover:bg-violet-700"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Book Classes
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => handleSubscriptionAction(sub.id, 'pause')}
                        size="sm"
                      >
                        <Pause className="w-4 h-4 mr-2" />
                        Pause
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleSubscriptionAction(sub.id, 'cancel')}
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Inactive Subscriptions */}
        {inactiveSubscriptions.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Past Subscriptions</h2>
            <div className="space-y-4">
              {inactiveSubscriptions.map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">{sub.package_name}</h3>
                        {getStatusBadge(sub.status)}
                      </div>
                      <p className="text-sm text-slate-600">
                        {sub.classes_used} of {sub.classes_total} classes used
                      </p>
                    </div>
                    {sub.status === 'paused' && (
                      <Button
                        onClick={() => handleSubscriptionAction(sub.id, 'resume')}
                        variant="outline"
                        size="sm"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Resume
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagement;
